import React, { useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { 
  FaClock, 
  FaCalendarAlt, 
  FaUsers, 
  FaStar,
  FaCheckCircle,
  FaBookOpen,
  FaCertificate,
  FaArrowRight,
  FaChartLine,
  FaDollarSign,
  FaShieldAlt,
  FaBalanceScale,
  FaFileMedical,
  FaClipboardList,
  FaExclamationTriangle,
  FaMedkit,
  FaHeartbeat,
  FaHospital
} from 'react-icons/fa';

const PageContainer = styled.div`
  margin-top: 200px;
  min-height: calc(100vh - 210px);
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);


  @media (max-width: 768px) {
    margin-top: 120px;
  }
`;

const HeroSection = styled.div`
  background: linear-gradient(135deg, #0a4d68 0%, #088395 50%, #0a2b3c 100%);
  color: white;
  padding: 4rem 5%;
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -50px;
    right: -50px;
    width: 300px;
    height: 300px;
    background: radial-gradient(circle, rgba(255, 217, 102, 0.1) 0%, transparent 70%);
    border-radius: 50%;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -50px;
    left: -50px;
    width: 300px;
    height: 300px;
    background: radial-gradient(circle, rgba(255, 217, 102, 0.1) 0%, transparent 70%);
    border-radius: 50%;
  }
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  margin-bottom: 1rem;
  position: relative;
  z-index: 2;
  
  span {
    color: #ffd966;
    display: block;
    font-size: 2rem;
    font-weight: 300;
    margin-top: 0.5rem;
  }
`;

const HeroDescription = styled.p`
  font-size: 1.2rem;
  max-width: 700px;
  margin: 0 auto 2rem;
  opacity: 0.95;
  position: relative;
  z-index: 2;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 4rem 5%;
`;

const SectionTitle = styled.h2`
  font-size: 2.2rem;
  color: #0a2b3c;
  margin-bottom: 2rem;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 80px;
    height: 4px;
    background: #ffd966;
    border-radius: 2px;
  }
`;

const Grid2Col = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: center;
  
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Grid3Col = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  
  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const ContentBlock = styled.div`
  p {
    color: #4a5f73;
    line-height: 1.8;
    margin-bottom: 1.5rem;
    font-size: 1.1rem;
  }
`;

const InfoCard = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(10, 77, 104, 0.05);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 35px rgba(10, 77, 104, 0.1);
    border-color: #ffd966;
  }
`;

const IconWrapper = styled.div`
  font-size: 2.5rem;
  color: #ffd966;
  margin-bottom: 1.5rem;
`;

const CardTitle = styled.h3`
  color: #0a2b3c;
  font-size: 1.3rem;
  margin-bottom: 1rem;
  font-weight: 600;
`;

const CardText = styled.p`
  color: #5a6b7a;
  line-height: 1.6;
  font-size: 0.95rem;
`;

const List = styled.ul`
  list-style: none;
  padding: 0;
`;

const ListItem = styled.li`
  margin-bottom: 0.8rem;
  color: #4a5f73;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  
  svg {
    color: #ffd966;
    font-size: 1rem;
  }
`;



const StatCard = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 10px;
  text-align: center;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(10, 77, 104, 0.05);
  
  .value {
    font-size: 2.2rem;
    font-weight: 700;
    color: #0a4d68;
    margin-bottom: 0.3rem;
  }
  
  .label {
    color: #5a6b7a;
    font-size: 0.9rem;
    text-transform: uppercase;
  }
`;

const CertificationBox = styled.div`
  background: linear-gradient(135deg, #0a4d68, #088395);
  border-radius: 20px;
  padding: 2.5rem;
  color: white;
  margin: 3rem 0;
  
  h3 {
    font-size: 1.8rem;
    margin-bottom: 1rem;
  }
  
  p {
    margin-bottom: 1.5rem;
    opacity: 0.9;
  }
`;

const ModelGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  margin: 2rem 0;
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const ModelCard = styled.div`
  background: #f8fafc;
  padding: 1.5rem;
  border-radius: 12px;
  border: 1px solid rgba(10, 77, 104, 0.05);
  
  h4 {
    color: #0a4d68;
    margin-bottom: 0.8rem;
    font-size: 1.1rem;
  }
`;

const CTASection = styled.div`
  background: linear-gradient(135deg, #0a4d68, #088395);
  border-radius: 20px;
  padding: 3rem;
  text-align: center;
  color: white;
  margin-top: 4rem;
  
  h3 {
    font-size: 2rem;
    margin-bottom: 1rem;
  }
  
  p {
    font-size: 1.1rem;
    margin-bottom: 2rem;
    opacity: 0.9;
  }
`;

const CTAButton = styled(Link)`
  background: #ffd966;
  color: #0a4d68;
  border: none;
  padding: 1rem 3rem;
  border-radius: 50px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  
  &:hover {
    background: white;
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  }
`;

const HCCRiskAdjustment = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

 

  const topHCCs = [
    "Diabetes with complications (HCC 18,19)",
    "Congestive heart failure (HCC 85)",
    "COPD (HCC 110)",
    "Chronic kidney disease (HCC 134-139)",
    "Vascular disease (HCC 106-108)",
    "Rheumatoid arthritis (HCC 40)"
  ];

  return (
    <PageContainer>
      <HeroSection>
        <HeroTitle>
          HCC & Risk Adjustment Coding
          <span>Master Hierarchical Condition Categories</span>
        </HeroTitle>
        <HeroDescription>
          Specialized training in risk adjustment coding for Medicare Advantage, ACA, and value-based 
          care models. Learn to accurately capture patient complexity and optimize RAF scores.
        </HeroDescription>
      </HeroSection>

      <Container>
      

        <Grid2Col>
          <ContentBlock>
            <SectionTitle>What is HCC Coding?</SectionTitle>
            <p>
              Hierarchical Condition Categories (HCC) are used by CMS to risk-adjust payments 
              in Medicare Advantage plans. Accurate HCC coding ensures that health plans receive 
              appropriate reimbursement based on patient acuity.
            </p>
            <p>
              This program covers the CMS-HCC model, RAF score calculation, documentation 
              requirements, RADV audits, and compliance. You'll learn to identify, code, and 
              document conditions that impact risk scores.
            </p>
          </ContentBlock>
          
          <InfoCard>
            <IconWrapper><FaChartLine /></IconWrapper>
            <CardTitle>Why HCC Coding?</CardTitle>
            <List>
              <ListItem><FaCheckCircle /> Fastest growing coding specialty</ListItem>
              <ListItem><FaCheckCircle /> Average salary: $70,000 - $90,000</ListItem>
              <ListItem><FaCheckCircle /> High demand in Medicare Advantage</ListItem>
              <ListItem><FaCheckCircle /> Work from home opportunities</ListItem>
              <ListItem><FaCheckCircle /> CRC certification preparation</ListItem>
            </List>
          </InfoCard>
        </Grid2Col>

        <CertificationBox>
          <h3>CRC Certification Preparation</h3>
          <p>
            This program fully prepares you for the AAPC CRC (Certified Risk Adjustment Coder) 
            examination. Master the CMS-HCC model, RAF scores, and risk adjustment guidelines.
          </p>
          <Grid2Col>
            <div>
              <h4 style={{ color: '#ffd966' }}>Exam Coverage:</h4>
              <List>
                <ListItem><FaCheckCircle /> CMS-HCC model (v24 & v28)</ListItem>
                <ListItem><FaCheckCircle /> RAF score calculation</ListItem>
                <ListItem><FaCheckCircle /> Documentation requirements</ListItem>
                <ListItem><FaCheckCircle /> RADV audit preparation</ListItem>
              </List>
            </div>
            <div>
              <h4 style={{ color: '#ffd966' }}>Practice Materials:</h4>
              <List>
                <ListItem><FaCheckCircle /> 400+ practice questions</ListItem>
                <ListItem><FaCheckCircle /> Real patient charts</ListItem>
                <ListItem><FaCheckCircle /> Mock exams</ListItem>
                <ListItem><FaCheckCircle /> Study guides</ListItem>
              </List>
            </div>
          </Grid2Col>
        </CertificationBox>

        <SectionTitle>Top HCC Categories</SectionTitle>
        <ModelGrid>
          {topHCCs.map((hcc, index) => (
            <ModelCard key={index}>
              <h4>{hcc.split('(')[0]}</h4>
              <small style={{ color: '#0a4d68' }}>{hcc.match(/\((.*?)\)/)?.[1]}</small>
            </ModelCard>
          ))}
        </ModelGrid>

        <Grid3Col>
          <InfoCard>
            <IconWrapper><FaBalanceScale /></IconWrapper>
            <CardTitle>RAF Scores</CardTitle>
            <CardText>Understand how risk adjustment factors are calculated and how multiple conditions combine to impact payment.</CardText>
          </InfoCard>
          <InfoCard>
            <IconWrapper><FaShieldAlt /></IconWrapper>
            <CardTitle>RADV Audits</CardTitle>
            <CardText>Prepare for risk adjustment data validation audits and ensure compliance with CMS requirements.</CardText>
          </InfoCard>
          <InfoCard>
            <IconWrapper><FaFileMedical /></IconWrapper>
            <CardTitle>Documentation</CardTitle>
            <CardText>Learn what documentation is required to support HCC coding and how to query providers effectively.</CardText>
          </InfoCard>
        </Grid3Col>

        <CTASection>
          <h3>Ready to Master Risk Adjustment?</h3>
          <p>Join our next batch and become a certified risk adjustment coder</p>
          <CTAButton to="/register">
            Enroll Now <FaArrowRight />
          </CTAButton>
        </CTASection>
      </Container>
    </PageContainer>
  );
};

export default HCCRiskAdjustment;